import {
  Children,
  Fragment,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
  useRef
} from 'react';
import { Modal as MuiModal, ModalProps, Typography } from '@mui/material';

import {
  AdminDialogBody,
  AdminDialogFooter,
  AdminDialogHeader
} from './AdminDialogShell';

let nextAdminDialogId = 0;

type AdminModalProps = ModalProps & {
  dialogTitle?: ReactNode;
};

const sxValue = (sx: unknown, key: string): unknown => {
  if (Array.isArray(sx)) {
    for (let index = sx.length - 1; index >= 0; index -= 1) {
      const value = sxValue(sx[index], key);
      if (value !== undefined) return value;
    }
  }
  if (sx && typeof sx === 'object' && key in sx) {
    return (sx as Record<string, unknown>)[key];
  }
  return undefined;
};

const isScrollableSurface = (surface: ReactElement<Record<string, any>>) => {
  const sx = surface.props.sx;
  const style = surface.props.style as Record<string, unknown> | undefined;
  const overflow = sxValue(sx, 'overflow') ?? style?.overflow;
  const overflowY = sxValue(sx, 'overflowY') ?? style?.overflowY;
  const maxHeight = sxValue(sx, 'maxHeight') ?? style?.maxHeight;
  return (
    Boolean(maxHeight) ||
    overflow === 'auto' ||
    overflow === 'scroll' ||
    overflowY === 'auto' ||
    overflowY === 'scroll'
  );
};

const containsButton = (node: ReactNode): boolean =>
  Children.toArray(node).some((child) => {
    if (!isValidElement(child)) return false;
    const type = child.type as
      | {
          muiName?: string;
          displayName?: string;
          name?: string;
          render?: { displayName?: string; name?: string };
        }
      | string;
    const componentName =
      typeof type === 'string'
        ? type
        : type.muiName ??
          type.displayName ??
          type.name ??
          type.render?.displayName ??
          type.render?.name;
    if (
      componentName === 'button' ||
      componentName === 'Button' ||
      componentName === 'IconButton'
    ) {
      return true;
    }
    return containsButton((child.props as { children?: ReactNode }).children);
  });

const flattenFragments = (node: ReactNode): ReactNode[] => {
  const result: ReactNode[] = [];
  Children.forEach(node, (child) => {
    if (
      child === null ||
      typeof child === 'boolean' ||
      (typeof child === 'string' && child.trim() === '')
    ) {
      return;
    }
    if (isValidElement(child) && child.type === Fragment) {
      result.push(
        ...flattenFragments((child.props as { children?: ReactNode }).children)
      );
      return;
    }
    result.push(child);
  });
  return result;
};

const componentName = (node: ReactElement<Record<string, any>>) => {
  const type = node.type as
    | {
        muiName?: string;
        displayName?: string;
        name?: string;
        render?: { displayName?: string; name?: string };
      }
    | string;
  return typeof type === 'string'
    ? type
    : type.muiName ??
        type.displayName ??
        type.name ??
        type.render?.displayName ??
        type.render?.name;
};

const isActionContainer = (node: ReactNode): boolean => {
  if (!isValidElement(node)) return false;
  const props = node.props as Record<string, any>;
  if (props['data-admin-dialog-actions']) return true;
  const sx = props.sx;
  const justify =
    props.justifyContent ??
    sxValue(sx, 'justifyContent') ??
    props.style?.justifyContent;
  const direction =
    props.direction ??
    sxValue(sx, 'flexDirection') ??
    props.style?.flexDirection;
  const display =
    sxValue(sx, 'display') ?? props.display ?? props.style?.display;
  const responsiveDirections =
    direction && typeof direction === 'object'
      ? Object.values(direction as Record<string, unknown>)
      : [];
  return (
    justify === 'flex-end' ||
    (containsButton(node) &&
      (justify === 'space-between' ||
        direction === 'row' ||
        responsiveDirections.includes('row') ||
        display === 'flex'))
  );
};

const responsiveWidth = (surface: ReactElement<Record<string, any>>) => {
  const configured =
    sxValue(surface.props.sx, 'width') ?? surface.props.style?.width;
  return {
    xs: 'calc(100vw - 32px)',
    sm: configured ?? 'min(960px, calc(100vw - 64px))'
  };
};

const splitDialogContent = (
  content: ReactNode,
  dialogTitle?: ReactNode
): { header: ReactNode; body: ReactNode[]; footer: ReactNode | null } => {
  const nodes = flattenFragments(content);
  let footerIndex = -1;
  for (let index = nodes.length - 1; index > 0; index -= 1) {
    if (isActionContainer(nodes[index])) {
      footerIndex = index;
      break;
    }
  }

  const header = dialogTitle ? (
    <Typography component="h2" variant="h6">
      {dialogTitle}
    </Typography>
  ) : (
    nodes[0]
  );
  const bodyStart = dialogTitle ? 0 : 1;
  const bodyEnd = footerIndex === -1 ? nodes.length : footerIndex;

  return {
    header,
    body: nodes.slice(bodyStart, bodyEnd),
    footer: footerIndex === -1 ? null : nodes[footerIndex]
  };
};

const canSplitLayoutContainer = (node: ReactNode): node is ReactElement => {
  if (!isValidElement(node)) return false;
  const name = componentName(node as ReactElement<Record<string, any>>);
  return name === 'Box' || name === 'Stack' || name === 'Grid';
};

const withStableKeys = (nodes: ReactNode[]) =>
  nodes.map((node, index) =>
    isValidElement(node) && node.key === null
      ? cloneElement(node, { key: `admin-dialog-content-${index}` })
      : node
  );

const splitNestedFooter = (
  body: ReactNode[]
): { body: ReactNode[]; footer: ReactNode | null } => {
  if (body.length !== 1 || !canSplitLayoutContainer(body[0])) {
    return { body, footer: null };
  }
  const container = body[0] as ReactElement<Record<string, any>>;
  const nested = flattenFragments(container.props.children);
  for (let index = nested.length - 1; index > 0; index -= 1) {
    if (isActionContainer(nested[index])) {
      return {
        body: [
          cloneElement(
            container,
            undefined,
            ...withStableKeys(nested.slice(0, index))
          )
        ],
        footer: nested[index]
      };
    }
  }
  return { body, footer: null };
};

const normalizeFormContent = (
  form: ReactElement<Record<string, any>>,
  dialogTitle: ReactNode | undefined,
  titleId: string
) => {
  const formChildren = flattenFragments(form.props.children);
  let content: ReactNode = formChildren;
  let bodyContainer: ReactElement<Record<string, any>> | null = null;

  if (formChildren.length === 1 && canSplitLayoutContainer(formChildren[0])) {
    bodyContainer = formChildren[0] as ReactElement<Record<string, any>>;
    content = bodyContainer.props.children;
  }

  const split = splitDialogContent(content, dialogTitle);
  const nested = split.footer
    ? { body: split.body, footer: split.footer }
    : splitNestedFooter(split.body);
  const body = bodyContainer
    ? [cloneElement(bodyContainer, undefined, ...withStableKeys(nested.body))]
    : nested.body;

  return cloneElement(
    form,
    {
      style: {
        ...form.props.style,
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden'
      }
    },
    <AdminDialogHeader id={titleId}>{split.header}</AdminDialogHeader>,
    <AdminDialogBody>{withStableKeys(body)}</AdminDialogBody>,
    nested.footer ? (
      <AdminDialogFooter>{nested.footer}</AdminDialogFooter>
    ) : null
  );
};

/**
 * Compatibility wrapper for the legacy MUI Modal + Box pattern.
 * Viewport-constrained panels receive persistent semantic chrome while short
 * overlays retain their original structure and behavior.
 */
const AdminModal = ({ children, dialogTitle, ...props }: AdminModalProps) => {
  const generatedTitleIdRef = useRef('');
  if (!generatedTitleIdRef.current) {
    nextAdminDialogId += 1;
    generatedTitleIdRef.current = `admin-dialog-${nextAdminDialogId}`;
  }
  const generatedTitleId = generatedTitleIdRef.current;
  if (!isValidElement(children)) {
    return <MuiModal {...props}>{children}</MuiModal>;
  }

  const surface = children as ReactElement<Record<string, any>>;
  const surfaceChildren = flattenFragments(surface.props.children);
  const shouldNormalize =
    isScrollableSurface(surface) ||
    Boolean(dialogTitle) ||
    surfaceChildren.length > 1;
  if (!shouldNormalize || surfaceChildren.length === 0) {
    return <MuiModal {...props}>{children}</MuiModal>;
  }

  const onlyChild = surfaceChildren.length === 1 ? surfaceChildren[0] : null;
  const isForm =
    isValidElement(onlyChild) &&
    componentName(onlyChild as ReactElement<Record<string, any>>) === 'form';
  const split = isForm
    ? null
    : splitDialogContent(surfaceChildren, dialogTitle);
  const nested =
    split && !split.footer
      ? splitNestedFooter(split.body)
      : { body: split?.body ?? [], footer: split?.footer ?? null };
  const labelledBy = generatedTitleId;

  const normalizedSurface = cloneElement(
    surface,
    {
      role: 'dialog',
      'aria-modal': true,
      'aria-labelledby': labelledBy,
      'data-admin-dialog-surface': true,
      sx: [
        surface.props.sx,
        {
          width: responsiveWidth(surface),
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100dvh - 32px)',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          overflowY: 'hidden',
          p: 0,
          outline: 'none'
        }
      ]
    },
    isForm ? (
      normalizeFormContent(
        onlyChild as ReactElement<Record<string, any>>,
        dialogTitle,
        generatedTitleId
      )
    ) : (
      <Fragment>
        <AdminDialogHeader id={generatedTitleId}>
          {split?.header}
        </AdminDialogHeader>
        <AdminDialogBody>{withStableKeys(nested.body)}</AdminDialogBody>
        {nested.footer ? (
          <AdminDialogFooter>{nested.footer}</AdminDialogFooter>
        ) : null}
      </Fragment>
    )
  );

  return <MuiModal {...props}>{normalizedSurface}</MuiModal>;
};

export default AdminModal;
